import { useRef, useEffect, useMemo, useCallback } from 'react'
import throttle from 'lodash.throttle'
import { format, isToday, isYesterday } from 'date-fns'
import { enUS, ru, uk } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import i18n from 'src/i18n'
import {
  useGetNotificationsInfiniteQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
} from 'src/redux/api'

const LOCALES = { en: enUS, ru, uk }

export const useNotifications = (limit: number = 6) => {
  const { t } = useTranslation()

  // Data fetching
  const {
    data: notificationPages,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
  } = useGetNotificationsInfiniteQuery({
    limit,
  })
  const notifications = useMemo(
    () => notificationPages?.pages.map((n) => n.notifications).flat() ?? [],
    [notificationPages?.pages],
  )

  const { data: unreadCount = 0 } = useGetUnreadCountQuery(undefined)

  // Infinite scroll cursor
  const observerRef = useRef<IntersectionObserver | null>(null)
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect()
      if (node) {
        observerRef.current = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting && notificationPages?.pages.at(-1)?.nextPage) {
              fetchNextPage()
            }
          },
          { threshold: 1 },
        )
        observerRef.current.observe(node)
      }
    },
    [fetchNextPage, notificationPages],
  )

  // Mark-as-read logic
  const [markAsReadApi] = useMarkAsReadMutation()
  const toMarkAsRead = useRef<Set<string>>(new Set())
  const alreadyMarkedAsRead = useRef<Set<string>>(new Set())
  const observerMarkRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    observerMarkRef.current = new IntersectionObserver(
      (entries) => {
        let hasChanges = false
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('data-id')
          if (!id) return
          if (entry.isIntersecting && !toMarkAsRead.current.has(id)) {
            toMarkAsRead.current.add(id)
            hasChanges = true
          }
        })
        if (hasChanges) throttleMark()
      },
      { threshold: 1.0 },
    )
    return () => observerMarkRef.current?.disconnect()
  }, [])

  const throttleMark = throttle(
    () => {
      const newIds = Array.from(toMarkAsRead.current).filter(
        (id) => !alreadyMarkedAsRead.current.has(id),
      )
      if (newIds.length) {
        newIds.forEach((id) => alreadyMarkedAsRead.current.add(id))
        markAsReadApi({ notificationIds: newIds })
      }
    },
    2000,
    { leading: false },
  )

  // Group by date and time
  const groupedNotifications = useMemo(
    () =>
      [...notifications]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .reduce(
          (acc, notification) => {
            const date = format(notification.createdAt, 'dd.MM.yyyy')
            const time = format(notification.createdAt, 'HH:mm')
            if (!acc[date]) acc[date] = {}
            if (!acc[date][time]) acc[date][time] = []
            acc[date][time].push(notification)
            return acc
          },
          {} as Record<string, Record<string, typeof notifications>>,
        ),
    [notifications],
  )

  function formatNotificationDate(dateString: string): string {
    const date = new Date(dateString)
    const lang = (i18n.language || 'en').split('-')[0] as keyof typeof LOCALES
    const locale = LOCALES[lang] || enUS

    if (isToday(date)) {
      return `${t('today')} ${format(date, 'HH:mm', { locale })}`
    }

    if (isYesterday(date)) {
      return `${t('yesterday')} ${format(date, 'HH:mm', { locale })}`
    }

    // Старее, чем вчера — дата и время
    return format(date, 'dd.MM.yyyy HH:mm', { locale })
    // return formatRelative(date, new Date(), { locale })
  }

  return {
    notifications,
    groupedNotifications,
    formatNotificationDate,
    unreadCount,
    isLoading,
    isError,
    refetch,
    lastElementRef,
    observerMarkRef,
    fetchNextPage,
  }
}
